import React from 'react';

import type { NavigationScreenSiteTabInsights } from '@gitbook/app-navigation';
import { LoadingPanel, Page, PageMain, PageWallpaper, Tab, Tabs } from '@gitbook/spine-react';

import {
    SiteInsightsAdsReport,
    SiteInsightsAskReport,
    SiteInsightsBrokenURLsReport,
    SiteInsightsLinksReport,
    SiteInsightsOpenAPIReport,
    SiteInsightsPagesReport,
    SiteInsightsSearchReport,
    SiteInsightsTrafficReport,
    siteInsightTabs,
} from '../../../../Components';
import { useSetAnalyticsScreen } from '../../../../navigation';
import { useSiteContext } from '../../../../state';
import darkBgGradient from '../../assets/site-settings-dark.raw.svg';
import lightBgGradient from '../../assets/site-settings-light.raw.svg';

type SiteInsightsProps = {
    tab: NavigationScreenSiteTabInsights;
};

/**
 * Site insights page to view and query the analytics of a site.
 */
export function SiteInsights(props: SiteInsightsProps) {
    const { tab } = props;
    const { site, organizationId } = useSiteContext();

    useSetAnalyticsScreen(
        {
            screen: 'site_insights',
            report: tab.report,
            range: tab.filters?.range,
            whereColumns: (tab.filters?.where ?? []).map((where) => where.column),
            groupByColumns: (tab.filters?.groupBy ?? []).map((groupBy) => groupBy.column),
        },
        [tab]
    );

    return (
        <Page className="flex-1">
            <PageWallpaper
                imageLight={lightBgGradient}
                imageDark={darkBgGradient}
                className="min-h-[calc(100vh-4rem)] bg-[calc(50%+12rem)_top] bg-[length:auto] bg-fixed"
            >
                <PageMain wide>
                    <div className="flex w-full gap-x-16">
                        <aside className="sticky top-2 hidden h-full w-[198px] shrink-0 align-self-start lg:block">
                            <Tabs orientation="vertical">
                                {siteInsightTabs.map((tabDefinition) => {
                                    if (tabDefinition.visible && !tabDefinition.visible(site)) {
                                        return null;
                                    }

                                    return (
                                        <Tab
                                            key={tabDefinition.id}
                                            leadingIcon={tabDefinition.icon}
                                            active={tabDefinition.id === tab.report}
                                            href={{
                                                current: {
                                                    tab: {
                                                        type: 'insights',
                                                        report: tabDefinition.id,
                                                    },
                                                },
                                            }}
                                            label={tabDefinition.label}
                                        />
                                    );
                                })}
                            </Tabs>
                        </aside>
                        <div className="flex w-full flex-col gap-6">
                            <React.Suspense fallback={<LoadingPanel size="large" />}>
                                {tab.report === 'traffic' ? (
                                    <SiteInsightsTrafficReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'openapi' ? (
                                    <SiteInsightsOpenAPIReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'links' ? (
                                    <SiteInsightsLinksReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'search' ? (
                                    <SiteInsightsSearchReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'ask' ? (
                                    <SiteInsightsAskReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'pages' ? (
                                    <SiteInsightsPagesReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'ads' ? (
                                    <SiteInsightsAdsReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                                {tab.report === 'urls' ? (
                                    <SiteInsightsBrokenURLsReport
                                        site={site}
                                        organizationId={organizationId}
                                    />
                                ) : null}
                            </React.Suspense>
                        </div>
                    </div>
                </PageMain>
            </PageWallpaper>
        </Page>
    );
}
